import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Loader2, Sparkles, Send, RefreshCw, DollarSign, Plus } from "lucide-react";
import api, { apiErr, fmtDate, money } from "@/lib/api";
import { Loading } from "@/components/site/ui";
import { Panel, Th, Td, adminInput } from "@/components/admin/ui";
import StatusBadge from "@/components/StatusBadge";
import { DocButtons } from "@/components/admin/DocButtons";
import SecurePayment from "@/components/admin/SecurePayment";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";

const RISK_STATUSES = ["Declined", "Cancelled", "No Show", "Payment Waived", "Complimentary"];
const PAY_METHODS = ["Cash", "Check", "Wire transfer", "Bank transfer", "Zelle", "Card by telephone", "Secure payment link", "External invoice", "Complimentary", "Host waived", "Other"];

function PaymentDialog({ rid, onDone }) {
  const [open, setOpen] = useState(false);
  const [f, setF] = useState({ amount: "", method: "Cash", kind: "payment", last_four: "", transaction_ref: "", check_number: "", notes: "", payment_date: "" });
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));
  const save = async () => {
    if (!f.amount) { toast.error("Enter an amount."); return; }
    setSaving(true);
    try { await api.post(`/admin/reservations/${rid}/payments`, { ...f, amount: parseFloat(f.amount) }); toast.success("Payment recorded."); setOpen(false); onDone(); }
    catch (e) { toast.error(apiErr(e)); } finally { setSaving(false); }
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild><button className="btn-gold px-4 py-2 text-sm flex items-center gap-2" data-testid="record-payment-btn"><DollarSign size={15} /> Record Payment</button></DialogTrigger>
      <DialogContent className="bg-surface border-white/10">
        <DialogHeader><DialogTitle className="font-serif text-2xl">Record Payment</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <input className={adminInput} placeholder="Amount" type="number" value={f.amount} onChange={(e) => set("amount", e.target.value)} data-testid="payment-amount" />
            <select className={adminInput} value={f.kind} onChange={(e) => set("kind", e.target.value)}>
              <option value="payment">Payment</option><option value="deposit">Deposit</option><option value="refund">Refund</option>
              <option value="adjustment">Adjustment</option><option value="waiver">Waiver</option>
            </select>
          </div>
          <select className={adminInput} value={f.method} onChange={(e) => set("method", e.target.value)} data-testid="payment-method">{PAY_METHODS.map((m) => <option key={m}>{m}</option>)}</select>
          <div className="grid grid-cols-2 gap-3">
            <input className={adminInput} placeholder="Last 4 (optional)" maxLength={4} value={f.last_four} onChange={(e) => set("last_four", e.target.value)} />
            <input className={adminInput} placeholder="Transaction / check ref" value={f.transaction_ref} onChange={(e) => set("transaction_ref", e.target.value)} />
          </div>
          <input type="date" className={adminInput} value={f.payment_date} onChange={(e) => set("payment_date", e.target.value)} />
          <textarea className={adminInput} rows={2} placeholder="Notes" value={f.notes} onChange={(e) => set("notes", e.target.value)} />
          <p className="text-[11px] text-muted-foreground">Never store full card numbers or CVV. Only the last four digits, entered manually, are kept.</p>
          <button onClick={save} disabled={saving} className="btn-gold w-full py-2.5 text-sm flex items-center justify-center gap-2" data-testid="payment-save">{saving ? <Loader2 className="animate-spin" size={15} /> : null} Save</button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function AIAssist({ rid }) {
  const [task, setTask] = useState("welcome_message");
  const [draft, setDraft] = useState("");
  const [loading, setLoading] = useState(false);
  const [enabled, setEnabled] = useState(true);
  const [subject, setSubject] = useState("A Message from The D");
  const [email, setEmail] = useState("");
  useEffect(() => { api.get("/ai/status").then((r) => setEnabled(r.data.enabled)).catch(() => setEnabled(false)); }, []);
  const gen = async () => {
    setLoading(true);
    try { const { data } = await api.post("/ai/assist", { task, context: { reservation_id: rid } }); setDraft(data.draft); }
    catch (e) { toast.error(apiErr(e)); } finally { setLoading(false); }
  };
  const sendDraft = async () => {
    if (!email) { toast.error("Enter recipient email."); return; }
    try { const r = await api.post("/admin/email/custom", { to: email, subject, body: draft, reservation_id: rid }); toast[r.data.sent ? "success" : "message"](r.data.sent ? "Email sent." : `Email ${r.data.status} (SMTP not configured).`); }
    catch (e) { toast.error(apiErr(e)); }
  };
  if (!enabled) return <Panel title="AI Assist"><p className="text-sm text-muted-foreground">AI features are disabled in Configuration.</p></Panel>;
  return (
    <Panel title={<span className="flex items-center gap-2"><Sparkles size={14} className="text-gold" /> AI Assist</span>}>
      <div className="flex gap-2 mb-3">
        <select className={adminInput} value={task} onChange={(e) => setTask(e.target.value)} data-testid="ai-task">
          {[["welcome_message", "Welcome message"], ["approval_message", "Approval message"], ["decline_message", "Decline message"], ["payment_reminder", "Payment reminder"], ["thank_you", "Thank-you"], ["arrival_summary", "Arrival summary"], ["guest_summary", "Guest preferences summary"]].map(([v, l]) => <option key={v} value={v}>{l}</option>)}
        </select>
        <button onClick={gen} disabled={loading} className="btn-ghost-gold px-4 text-sm flex items-center gap-2 whitespace-nowrap" data-testid="ai-generate">{loading ? <Loader2 className="animate-spin" size={14} /> : <RefreshCw size={14} />} Draft</button>
      </div>
      {draft && (
        <div className="space-y-3">
          <div className="text-[10px] tracking-label uppercase text-gold">AI-generated draft · review before sending</div>
          <textarea className={adminInput} rows={7} value={draft} onChange={(e) => setDraft(e.target.value)} data-testid="ai-draft" />
          <div className="grid grid-cols-2 gap-2">
            <input className={adminInput} placeholder="Recipient email" value={email} onChange={(e) => setEmail(e.target.value)} />
            <input className={adminInput} placeholder="Subject" value={subject} onChange={(e) => setSubject(e.target.value)} />
          </div>
          <button onClick={sendDraft} className="btn-gold px-4 py-2 text-sm flex items-center gap-2" data-testid="ai-send"><Send size={14} /> Send as Email</button>
        </div>
      )}
    </Panel>
  );
}

export default function AdminReservationDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const [r, setR] = useState(null);
  const [statuses, setStatuses] = useState([]);
  const [sc, setSc] = useState({ status: "", internal_note: "", guest_note: "", reason: "" });
  const [item, setItem] = useState({ label: "", amount: "", kind: "charge" });
  const load = () => api.get(`/admin/reservations/${id}`).then((res) => { setR(res.data); setSc((p) => ({ ...p, status: res.data.status })); }).catch(() => setR(false));
  useEffect(() => { load(); api.get("/admin/config/reservation_statuses").then((r) => setStatuses(r.data.value || [])).catch(() => {}); }, [id]);

  if (r === null) return <Loading />;
  if (!r) return <p className="text-muted-foreground">Not found.</p>;

  const changeStatus = async () => {
    if (RISK_STATUSES.includes(sc.status) && !sc.reason.trim()) { toast.error("A reason is required for this status."); return; }
    try { await api.post(`/admin/reservations/${id}/status`, sc); toast.success(`Status: ${sc.status}`); load(); }
    catch (e) { toast.error(apiErr(e)); }
  };
  const addItem = async () => {
    if (!item.label || !item.amount) return;
    try { await api.post(`/admin/reservations/${id}/folio-item`, { ...item, amount: parseFloat(item.amount) }); toast.success("Folio updated."); setItem({ label: "", amount: "", kind: "charge" }); load(); }
    catch (e) { toast.error(apiErr(e)); }
  };
  const approveCheckin = async (status) => { try { await api.post(`/admin/reservations/${id}/checkin-approve`, { status }); toast.success("Check-in " + status); load(); } catch (e) { toast.error(apiErr(e)); } };

  return (
    <div className="space-y-6">
      <button onClick={() => nav("/admin/reservations")} className="text-sm text-muted-foreground flex items-center gap-2 hover:text-pool"><ArrowLeft size={15} /> Reservations</button>
      <div className="flex justify-between items-start flex-wrap gap-4">
        <div>
          <p className="text-xs tracking-label uppercase text-muted-foreground">{r.confirmation_number}</p>
          <h1 className="font-serif text-3xl mt-1">{r.experience_title}</h1>
          <p className="text-sm text-muted-foreground mt-2">{r.contact_email} · {fmtDate(r.arrival_date)} → {fmtDate(r.departure_date)} · {r.adults} adults / {r.children} children</p>
        </div>
        <div className="text-right space-y-2"><StatusBadge status={r.status} /><p className="text-xs text-muted-foreground">Check-in: {r.checkin_status}</p></div>
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          {/* Folio */}
          <Panel title="Folio & Ledger" action={<PaymentDialog rid={id} onDone={load} />}>
            <table className="w-full text-sm mb-4">
              <tbody>
                {r.folio.items.map((it) => (<tr key={it.id}><Td>{it.label}</Td><Td className={`text-right ${it.kind === "discount" ? "text-pool" : ""}`}>{money(it.amount)}</Td></tr>))}
              </tbody>
            </table>
            <div className="flex gap-2 mb-4">
              <input className={adminInput} placeholder="Line item" value={item.label} onChange={(e) => setItem((p) => ({ ...p, label: e.target.value }))} />
              <input className={adminInput + " w-28"} placeholder="Amount" type="number" value={item.amount} onChange={(e) => setItem((p) => ({ ...p, amount: e.target.value }))} />
              <select className={adminInput + " w-32"} value={item.kind} onChange={(e) => setItem((p) => ({ ...p, kind: e.target.value }))}><option value="charge">Charge</option><option value="discount">Discount</option><option value="fee">Fee</option></select>
              <button onClick={addItem} className="btn-ghost-gold px-3" data-testid="folio-add-item"><Plus size={15} /></button>
            </div>
            <div className="border-t border-white/10 pt-3 space-y-1 text-sm">
              <div className="flex justify-between"><span className="text-muted-foreground">Total</span><span>{money(r.folio.total)}</span></div>
              <div className="flex justify-between"><span className="text-muted-foreground">Paid</span><span>{money(r.folio.paid)}</span></div>
              {parseFloat(r.folio.refunded) > 0 && <div className="flex justify-between"><span className="text-muted-foreground">Refunded</span><span>{money(r.folio.refunded)}</span></div>}
              <div className="flex justify-between font-medium"><span>Balance</span><span className="text-gold">{money(r.folio.balance)}</span></div>
            </div>
            {r.folio.payments.length > 0 && (
              <div className="mt-4"><p className="text-[10px] tracking-label uppercase text-muted-foreground mb-2">Payments</p>
                {r.folio.payments.map((p) => (<div key={p.id} className="flex justify-between text-xs py-1 border-t border-white/5"><span>{fmtDate(p.payment_date)} · {p.method} {p.last_four ? `••${p.last_four}` : ""} <span className="text-muted-foreground">({p.kind})</span></span><span>{money(p.amount)}</span></div>))}
              </div>
            )}
          </Panel>

          <SecurePayment rid={id} />

          <AIAssist rid={id} />

          <Panel title="Documents · PDF & Print">
            <DocButtons testidPrefix="res-doc" docs={[
              { key: "confirmation", label: "Reservation Confirmation", path: `/admin/documents/reservation/${id}/confirmation`, filename: `${r.confirmation_number}-confirmation.pdf` },
              { key: "folio", label: "Folio Statement", path: `/admin/documents/reservation/${id}/folio`, filename: `${r.confirmation_number}-folio.pdf` },
              { key: "receipt", label: "Payment Receipt", path: `/admin/documents/reservation/${id}/receipt`, filename: `${r.confirmation_number}-receipt.pdf` },
              { key: "deposit-request", label: "Deposit Request", path: `/admin/documents/reservation/${id}/deposit-request`, filename: `${r.confirmation_number}-deposit.pdf` },
              { key: "balance-statement", label: "Balance Statement", path: `/admin/documents/reservation/${id}/balance-statement`, filename: `${r.confirmation_number}-balance.pdf` },
              { key: "final-receipt", label: "Final Receipt", path: `/admin/documents/reservation/${id}/final-receipt`, filename: `${r.confirmation_number}-final-receipt.pdf` },
              { key: "checkin-record", label: "Digital Check-In Record", path: `/admin/documents/reservation/${id}/checkin-record`, filename: `${r.confirmation_number}-checkin.pdf` },
              { key: "policy-acknowledgment", label: "Signed Policy Acknowledgment", path: `/admin/documents/reservation/${id}/policy-acknowledgment`, filename: `${r.confirmation_number}-policies.pdf` },
            ]} />
          </Panel>

          {/* Communications */}
          <Panel title="Communications">
            {r.communications.length === 0 ? <p className="text-sm text-muted-foreground">No emails yet.</p> : (
              <div className="space-y-2">{r.communications.map((c) => (
                <div key={c.id} className="flex justify-between text-sm py-1.5 border-b border-white/5"><span>{c.subject}</span><StatusBadge status={c.status === "not_configured" ? "Not Configured" : c.status} /></div>
              ))}</div>
            )}
          </Panel>

          {/* Status history */}
          <Panel title="Status History">
            <div className="space-y-2">{(r.status_history || []).slice().reverse().map((h) => (
              <div key={h.id} className="text-sm border-l-2 border-pool/40 pl-3 py-1">
                <span className="text-pool">{h.previous_status || "—"} → {h.new_status}</span>
                <span className="text-xs text-muted-foreground ml-2">{fmtDate(h.at)} · {h.changed_by}</span>
                {h.internal_note && <p className="text-xs text-muted-foreground">{h.internal_note}</p>}
              </div>
            ))}</div>
          </Panel>
        </div>

        <div className="space-y-6">
          <Panel title="Change Status">
            <select className={adminInput + " mb-2"} value={sc.status} onChange={(e) => setSc((p) => ({ ...p, status: e.target.value }))} data-testid="status-select">
              {statuses.map((s) => <option key={s}>{s}</option>)}
            </select>
            <input className={adminInput + " mb-2"} placeholder="Internal note" value={sc.internal_note} onChange={(e) => setSc((p) => ({ ...p, internal_note: e.target.value }))} />
            <input className={adminInput + " mb-2"} placeholder="Guest-visible note (optional)" value={sc.guest_note} onChange={(e) => setSc((p) => ({ ...p, guest_note: e.target.value }))} />
            {RISK_STATUSES.includes(sc.status) && <input className={adminInput + " mb-2"} placeholder="Reason (required)" value={sc.reason} onChange={(e) => setSc((p) => ({ ...p, reason: e.target.value }))} data-testid="status-reason" />}
            <button onClick={changeStatus} className="btn-gold w-full py-2 text-sm" data-testid="status-apply">Apply Status</button>
          </Panel>

          <Panel title="Digital Check-In">
            <p className="text-sm mb-3">Status: <StatusBadge status={r.checkin_status} /></p>
            {r.checkin && <div className="text-xs text-muted-foreground space-y-1 mb-3">
              <p>Signed: {r.checkin.signed_name || "—"}</p>
              <p>Arrival: {r.checkin.arrival_time || "—"}</p>
              {r.checkin.vehicles?.length > 0 && <p>Vehicles: {r.checkin.vehicles.join(", ")}</p>}
            </div>}
            <div className="flex gap-2">
              <button onClick={() => approveCheckin("Approved")} className="btn-ghost-gold px-3 py-1.5 text-xs flex-1" data-testid="checkin-approve">Approve</button>
              <button onClick={() => approveCheckin("Waived by Host")} className="btn-ghost-gold px-3 py-1.5 text-xs flex-1">Waive</button>
            </div>
          </Panel>

          <Panel title="Guest Details">
            <div className="text-sm space-y-1.5 text-muted-foreground">
              <p><span className="text-foreground">Email:</span> {r.contact_email}</p>
              <p><span className="text-foreground">Phone:</span> {r.contact_phone || "—"}</p>
              <p><span className="text-foreground">Guests:</span> {(r.guest_names || []).join(", ") || "—"}</p>
              <p><span className="text-foreground">Vehicle:</span> {r.vehicle || "—"}</p>
              <p><span className="text-foreground">Requests:</span> {r.special_requests || "—"}</p>
              <p><span className="text-foreground">Source:</span> {r.source}</p>
            </div>
          </Panel>
        </div>
      </div>
    </div>
  );
}
